import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { memberAuthAPI } from '../../utils/api';
import LogoImage from '../../components/ui/LogoImage';
import { useMemberAuth } from '../../context/MemberAuthContext';
import { toast } from 'react-toastify';

// ══════════════════════════════════════════════════════════════
// EMAIL VERIFICATION PAGE  (/verify-email?token=xxx)
// ══════════════════════════════════════════════════════════════
export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus]   = useState('loading'); // loading | success | error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) { setStatus('error'); setMessage('No verification token found.'); return; }
    memberAuthAPI.verifyEmail(token)
      .then(r => { setStatus('success'); setMessage(r.data.message); })
      .catch(err => { setStatus('error'); setMessage(err.response?.data?.message || 'Verification failed.'); });
  }, []);

  return (
    <div className="min-h-screen pattern-bg flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-8 text-center">
        {status === 'loading' && (
          <>
            <div className="h-14 w-14 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-5" />
            <p className="text-navy-600 font-medium">Verifying your email...</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="text-5xl mb-4">✅</div>
            <h2 className="text-xl font-bold text-navy-800 mb-3">Email Verified!</h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <div className="bg-cyan-50 rounded-2xl p-4 text-sm text-cyan-800 mb-6">
              <p>⏳ Your membership is now <strong>under review</strong>. You will receive an email once the admin approves your account.</p>
            </div>
            <Link to="/" className="btn-primary inline-block">Back to Home</Link>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="text-5xl mb-4">❌</div>
            <h2 className="text-xl font-bold text-navy-800 mb-3">Verification Failed</h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <Link to="/member/register" className="btn-primary inline-block">Register Again</Link>
          </>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MEMBER LOGIN PAGE
// ══════════════════════════════════════════════════════════════
export function MemberLoginPage() {
  const { login }   = useMemberAuth();
  const navigate    = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrorMsg('');
    try {
      await login(form.email, form.password);
      toast.success('Welcome back!');
      navigate('/member/dashboard');
    } catch (err) {
      const msg = err.response?.data?.message || 'Login failed.';
      const reason = err.response?.data?.reason;
      setErrorMsg(msg + (reason ? ` Reason: ${reason}` : ''));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen pattern-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="flex justify-center mb-3">
            <LogoImage size={64} className="rounded-full border-4 border-cyan-400 shadow-lg" />
          </div>
          <h1 className="text-2xl font-bold text-navy-800">Member Login</h1>
          <p className="text-gray-500 text-sm mt-1">Access your JNF member dashboard</p>
        </div>
        <div className="bg-white rounded-3xl shadow-lg p-8">
          {errorMsg && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl p-4 text-sm mb-5">
              {errorMsg}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="form-label">Email Address</label>
              <input required type="email" className="form-input" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="your@email.com" />
            </div>
            <div>
              <label className="form-label">Password</label>
              <input required type="password" className="form-input" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="••••••••" />
            </div>
            <div className="text-right">
              <Link to="/member/forgot-password" className="text-xs text-cyan-600 hover:underline">Forgot password?</Link>
            </div>
            <button type="submit" disabled={saving} className="btn-primary w-full flex items-center justify-center gap-2">
              {saving ? <><div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Signing in...</> : 'Sign In'}
            </button>
          </form>
          <div className="mt-6 text-center text-sm text-gray-500 space-y-1">
            <p>Not a member yet? <Link to="/member/register" className="text-cyan-600 font-semibold hover:underline">Register here</Link></p>
            <p><Link to="/" className="text-gray-400 hover:text-navy-600">← Back to Website</Link></p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// FORGOT PASSWORD
// ══════════════════════════════════════════════════════════════
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent]   = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await memberAuthAPI.forgotPassword({ email });
      setSent(true);
    } catch { toast.error('Something went wrong.'); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen pattern-bg flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-8">
        {sent ? (
          <div className="text-center">
            <div className="text-5xl mb-4">📧</div>
            <h2 className="text-xl font-bold text-navy-800 mb-2">Reset Link Sent</h2>
            <p className="text-gray-600 text-sm mb-6">If an account exists for <strong>{email}</strong>, a password reset link has been sent. Check your inbox.</p>
            <Link to="/member/login" className="btn-primary inline-block">Back to Login</Link>
          </div>
        ) : (
          <>
            <h2 className="text-xl font-bold text-navy-800 mb-1">Forgot Password?</h2>
            <p className="text-gray-500 text-sm mb-6">Enter your email and we'll send you a reset link.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="form-label">Email Address</label>
                <input required type="email" className="form-input" value={email} onChange={e => setEmail(e.target.value)} />
              </div>
              <button type="submit" disabled={saving} className="btn-primary w-full">{saving ? 'Sending...' : 'Send Reset Link'}</button>
            </form>
            <p className="text-center mt-4 text-sm"><Link to="/member/login" className="text-cyan-600 hover:underline">← Back to Login</Link></p>
          </>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// RESET PASSWORD  (/member/reset-password?token=xxx)
// ══════════════════════════════════════════════════════════════
export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm]   = useState({ password: '', confirm: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) return toast.error('Passwords do not match.');
    setSaving(true);
    try {
      await memberAuthAPI.resetPassword({ token: searchParams.get('token'), password: form.password });
      toast.success('Password reset! Please log in.');
      navigate('/member/login');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Reset failed.');
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen pattern-bg flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-8">
        <h2 className="text-xl font-bold text-navy-800 mb-1">Set New Password</h2>
        <p className="text-gray-500 text-sm mb-6">Choose a new secure password for your account.</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="form-label">New Password</label>
            <input required type="password" className="form-input" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Min. 8 characters" />
          </div>
          <div>
            <label className="form-label">Confirm Password</label>
            <input required type="password" className="form-input" value={form.confirm} onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} />
          </div>
          <button type="submit" disabled={saving} className="btn-primary w-full">{saving ? 'Resetting...' : 'Reset Password'}</button>
        </form>
      </div>
    </div>
  );
}
